/**
 * Shared formatting utilities for consistent display across the app
 */

/**
 * Format a number as USD currency
 * @param {number} amount - The amount to format
 * @param {boolean} showCents - Whether to show cents (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showCents = false) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(amount || 0);
};

/**
 * Format a number as compact currency (e.g., $1.2M)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted compact currency string
 */
export const formatCompactCurrency = (amount) => {
  if (Math.abs(amount) >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(amount) >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return formatCurrency(amount);
};

/**
 * Format a date string to MM/DD/YYYY format
 * @param {string} dateStr - ISO date string or YYYY-MM-DD
 * @param {object} options - Intl.DateTimeFormat options (overrides default)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateStr, options = {}) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + (dateStr.length === 10 ? "T00:00:00" : ""));
  if (isNaN(date.getTime())) return dateStr;
  
  // If custom options passed, use those; otherwise default to MM/DD/YYYY
  if (Object.keys(options).length > 0) {
    return date.toLocaleDateString("en-US", options);
  }
  
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
};

/**
 * Format a date string to short format (MM/DD/YYYY)
 * @param {string} dateStr - ISO date string or YYYY-MM-DD
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + (dateStr.length === 10 ? "T00:00:00" : ""));
  if (isNaN(date.getTime())) return dateStr;
  
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
};

/**
 * Format a datetime string with time (MM/DD/YYYY HH:MM AM/PM)
 * @param {string} isoStr - ISO datetime string
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (isoStr) => {
  if (!isoStr) return "";
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return isoStr;
  
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const y = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${m}/${d}/${y} ${time}`;
};

/**
 * Format a number as percentage
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, decimals = 1) => {
  return `${(value || 0).toFixed(decimals)}%`;
};

/**
 * Format a number with thousand separators
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value || 0);
};

/**
 * Format hours as decimal (e.g., 8.5)
 * @param {number} hours - The hours value
 * @returns {string} Formatted hours string
 */
export const formatHours = (hours) => {
  return (hours || 0).toFixed(1);
};

/**
 * Format a phone number
 * @param {string} phone - Phone number string
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// Alias for backward compatibility
export const formatPhoneNumber = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// True when a phone value contains exactly 10 digits (US number).
export const isCompletePhone = (value) => (value || "").replace(/\D/g, "").length === 10;

/**
 * Auto-format a date as the user types into MM/DD/YYYY
 * @param {string} value - Raw input value
 * @returns {string} Partial or full MM/DD/YYYY string
 */
export const formatDateInput = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

// MM/DD/YYYY ⇄ ISO (YYYY-MM-DD). Return "" when the input is incomplete or not a real date.
export const mmddyyyyToIso = (value) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || "");
  if (!m) return "";
  const [, mm, dd, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  const valid = d.getFullYear() === Number(yyyy) && d.getMonth() === Number(mm) - 1 && d.getDate() === Number(dd);
  return valid ? `${yyyy}-${mm}-${dd}` : "";
};

export const isoToMmddyyyy = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
};

// Whole years between an ISO birth date and today; null when unknown or in the future.
export const calculateAge = (iso, today = new Date()) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < mo || (today.getMonth() + 1 === mo && today.getDate() < d)) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

// Split "John Smith Jr." → { first_name: "John", last_name: "Smith Jr." }
export const splitFullName = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: "", last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
};

/**
 * Auto-format a Social Security Number as the user types into xxx-xx-xxxx
 * @param {string} value - Raw input value
 * @returns {string} Partial or full xxx-xx-xxxx string
 */
export const formatSSN = (value) => {
  if (!value) return "";
  const d = value.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

/**
 * Get relative time string (e.g., "2 hours ago")
 * @param {string} dateStr - ISO datetime string
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  
  return formatDate(dateStr);
};

export const phoneHref = (phone) => {
  let digits = (phone || "").replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (!digits.startsWith("+")) digits = (digits.length === 10 ? "+1" : digits.length === 11 && digits[0] === "1" ? "+" : "") + digits;
  // Standard tel: link — the OS opens (or asks for) the default calling app: Zoom Phone, FaceTime, Teams, or the phone dialer.
  return `tel:${digits}`;
};
